import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Search, Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminAuth';
import { effectiveUnitPrice } from '../../utils/pricing';

const API_URL = '';

export default function ExchangeModal({ order, onClose, onSuccess }) {
  const [returnedSelection, setReturnedSelection] = useState({}); // idx -> qty (defaults to item.quantity)
  const [newItems, setNewItems] = useState([]); // [{product_id, name, size, color, quantity, price, image_url, variants}]
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [wilayas, setWilayas] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [wilaya, setWilaya] = useState(
    (order?.wilaya_code && /^\d+$/.test(String(order.wilaya_code))) ? String(order.wilaya_code) : ''
  );
  const [commune, setCommune] = useState(order?.commune || '');
  const [shippingAddress, setShippingAddress] = useState(order?.shipping_address || '');
  const [deliveryType, setDeliveryType] = useState('home');
  const [fees, setFees] = useState({ tarif_domicile: 0, tarif_bureau: 0 });
  const [freeShipping, setFreeShipping] = useState(false);
  const [freeReason, setFreeReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('credit');
  const [shipmentType, setShipmentType] = useState('exchange'); // "normal" | "exchange"
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(''); // persistent error, survives toast dismiss

  // Fetch wilayas
  useEffect(() => {
    fetch(`${API_URL}/api/ecotrack/wilayas`).then(r => r.json()).then(d => setWilayas(d.wilayas || []));
  }, []);

  // Fetch communes + fees on wilaya change. Guard against any non-numeric wilaya
  // (e.g. legacy "wilaya_code: '-'" on some prod orders that previously fired /-).
  useEffect(() => {
    if (!wilaya || !/^\d+$/.test(String(wilaya))) {
      setCommunes([]);
      setFees({ tarif_domicile: 0, tarif_bureau: 0 });
      return;
    }
    fetch(`${API_URL}/api/ecotrack/communes/${wilaya}`).then(r => r.json()).then(d => setCommunes(d.communes || []));
    fetch(`${API_URL}/api/ecotrack/fees/${wilaya}`).then(r => r.json()).then(d => setFees(d || {}));
  }, [wilaya]);

  // Search products with debounce
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(search)}&limit=10`);
        const d = await r.json();
        setSearchResults(d.products || []);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleReturn = (idx, qty) => {
    setReturnedSelection(prev => {
      const next = { ...prev };
      if (next[idx] !== undefined) delete next[idx];
      else next[idx] = qty || 1;
      return next;
    });
  };

  const addNewItem = (product, size, color) => {
    const variant = (product.variants || []).find(v => v.size === size && (!color || !v.color || v.color === color));
    if (!variant) return toast.error('Variante introuvable');
    if ((variant.stock || 0) < 1) return toast.error('Rupture de stock');
    const price = effectiveUnitPrice(product, variant);
    setNewItems(prev => [...prev, {
      product_id: product.product_id,
      name: product.name,
      size,
      color: color || '',
      quantity: 1,
      price,
      image_url: product.images?.[0]?.url || '',
      max_stock: variant.stock,
    }]);
    setSearch('');
    setSearchResults([]);
  };

  const updateNewItemQty = (i, delta) => {
    setNewItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const newQty = Math.max(1, Math.min(it.max_stock || 99, it.quantity + delta));
      return { ...it, quantity: newQty };
    }));
  };

  const removeNewItem = (i) => setNewItems(prev => prev.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    const returnedItems = (order?.items || []).reduce((acc, item, idx) => {
      if (returnedSelection[idx] !== undefined) {
        acc.push({ ...item, quantity: returnedSelection[idx] });
      }
      return acc;
    }, []);
    const returnedTotal = returnedItems.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
    const newTotal = newItems.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
    const diff = newTotal - returnedTotal;
    const shippingCost = freeShipping ? 0 : (deliveryType === 'bureau' ? (fees.tarif_bureau || 0) : (fees.tarif_domicile || 0));
    const customerPays = Math.max(0, diff) + shippingCost;
    const refundAmount = diff < 0 ? Math.abs(diff) : 0;
    return { returnedItems, returnedTotal, newTotal, diff, shippingCost, customerPays, refundAmount };
  }, [returnedSelection, newItems, order, deliveryType, fees, freeShipping]);

  const canSubmit = newItems.length > 0 && wilaya && commune && shippingAddress && (!freeShipping || freeReason.trim());

  const handleSubmit = async () => {
    if (!canSubmit) return toast.error('Verifiez tous les champs requis');
    setSubmitting(true);
    setSubmitError('');
    try {
      const body = {
        returned_items: totals.returnedItems.map(it => ({
          product_id: it.product_id, name: it.name, size: it.size,
          color: it.color || null, quantity: it.quantity, price: it.price,
        })),
        new_items: newItems.map(it => ({
          product_id: it.product_id, size: it.size,
          color: it.color || null, quantity: it.quantity,
        })),
        wilaya, commune, shipping_address: shippingAddress, delivery_type: deliveryType,
        free_shipping: freeShipping,
        free_shipping_reason: freeShipping ? freeReason : null,
        refund_method: totals.refundAmount > 0 ? refundMethod : null,
        shipment_type: shipmentType,
      };
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${order.order_id}/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(`Echange #${data.exchange_order_id} cree et expedie !`);
      if (data.order?.ecotrack_tracking) toast.success(`Tracking: ${data.order.ecotrack_tracking}`);
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error('Exchange error:', e);
      setSubmitError(e.message || 'Erreur inconnue');
      toast.error(`Erreur : ${e.message}`);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" data-testid="exchange-modal">
      <div className="min-h-screen p-4 flex items-start justify-center">
        <div className="bg-white max-w-4xl w-full p-6 my-8 rounded">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#E0DFDD]">
            <h2 className="font-serif text-2xl">Créer un échange — Commande #{order.order_id}</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F9F8F6]"><X size={20} /></button>
          </div>

          {/* Step 1: Select returned items */}
          <section className="mb-6">
            <h3 className="text-sm font-medium mb-3 uppercase tracking-wide text-[#6B6A68]">1. Articles retournés</h3>
            <div className="space-y-2">
              {(order.items || []).map((it, idx) => (
                <label key={idx} className="flex items-center gap-3 p-3 border border-[#E0DFDD] cursor-pointer hover:bg-[#F9F8F6]">
                  <input
                    type="checkbox"
                    checked={returnedSelection[idx] !== undefined}
                    onChange={() => toggleReturn(idx, it.quantity)}
                    className="w-4 h-4 accent-[#1A1514]"
                    data-testid={`return-item-${idx}`}
                  />
                  {it.image_url && <img src={it.image_url} alt="" className="w-12 h-12 object-cover" />}
                  <div className="flex-1">
                    <div className="text-sm font-medium">{it.name}</div>
                    <div className="text-xs text-[#6B6A68]">Taille {it.size}{it.color ? ` / ${it.color}` : ''} × {it.quantity}</div>
                  </div>
                  <div className="text-sm font-medium">{(it.price * it.quantity).toLocaleString()} DA</div>
                </label>
              ))}
            </div>
          </section>

          {/* Step 2: Add new items */}
          <section className="mb-6">
            <h3 className="text-sm font-medium mb-3 uppercase tracking-wide text-[#6B6A68]">2. Articles d'échange</h3>
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un produit..."
                className="w-full pl-9 pr-3 py-2 border border-[#E0DFDD] text-sm"
                data-testid="exchange-product-search"
              />
              {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
            </div>
            {searchResults.length > 0 && (
              <div className="border border-[#E0DFDD] max-h-64 overflow-y-auto mb-3">
                {searchResults.map(p => (
                  <ProductPicker key={p.product_id} product={p} onPick={(size, color) => addNewItem(p, size, color)} />
                ))}
              </div>
            )}

            {newItems.length > 0 && (
              <div className="space-y-2">
                {newItems.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-[#E0DFDD]" data-testid={`new-item-${i}`}>
                    {it.image_url && <img src={it.image_url} alt="" className="w-12 h-12 object-cover" />}
                    <div className="flex-1">
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="text-xs text-[#6B6A68]">Taille {it.size}{it.color ? ` / ${it.color}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateNewItemQty(i, -1)} className="p-1 border border-[#E0DFDD] hover:bg-[#F9F8F6]"><Minus size={12} /></button>
                      <span className="text-sm w-6 text-center">{it.quantity}</span>
                      <button onClick={() => updateNewItemQty(i, 1)} className="p-1 border border-[#E0DFDD] hover:bg-[#F9F8F6]"><Plus size={12} /></button>
                    </div>
                    <div className="text-sm font-medium w-20 text-right">{(it.price * it.quantity).toLocaleString()} DA</div>
                    <button onClick={() => removeNewItem(i)} className="p-1 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Step 3: Delivery */}
          <section className="mb-6">
            <h3 className="text-sm font-medium mb-3 uppercase tracking-wide text-[#6B6A68]">3. Livraison</h3>
            {/* Shipment type — controls World Express type=1 vs type=2 */}
            <div className="flex gap-2 mb-3" data-testid="shipment-type-toggle">
              <label className={`flex-1 cursor-pointer border p-2 text-sm text-center ${shipmentType === 'normal' ? 'border-[#1A1514] bg-[#F9F8F6] font-medium' : 'border-[#E0DFDD]'}`}>
                <input type="radio" name="shipment-type" value="normal" className="hidden" checked={shipmentType === 'normal'} onChange={() => setShipmentType('normal')} data-testid="shipment-type-normal" />
                Livraison normale
              </label>
              <label className={`flex-1 cursor-pointer border p-2 text-sm text-center ${shipmentType === 'exchange' ? 'border-[#1A1514] bg-[#F9F8F6] font-medium' : 'border-[#E0DFDD]'}`}>
                <input type="radio" name="shipment-type" value="exchange" className="hidden" checked={shipmentType === 'exchange'} onChange={() => setShipmentType('exchange')} data-testid="shipment-type-exchange" />
                Échange (2 bordereaux)
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <select value={wilaya} onChange={e => setWilaya(e.target.value)} className="border border-[#E0DFDD] p-2 text-sm" data-testid="exchange-wilaya">
                <option value="">Selectionner une wilaya</option>
                {wilayas.map(w => <option key={w.wilaya_id} value={w.wilaya_id}>{w.wilaya_id} - {w.wilaya_name}</option>)}
              </select>
              <select value={commune} onChange={e => setCommune(e.target.value)} className="border border-[#E0DFDD] p-2 text-sm" data-testid="exchange-commune">
                <option value="">{deliveryType === 'bureau' ? 'Selectionner une commune avec bureau' : 'Selectionner une commune'}</option>
                {(deliveryType === 'bureau' ? communes.filter(c => c.has_stop_desk === 1) : communes).map(c => (
                  <option key={c.nom} value={c.nom}>{c.nom}{c.has_stop_desk ? ' (bureau)' : ''}</option>
                ))}
              </select>
            </div>
            <input type="text" value={shippingAddress} onChange={e => setShippingAddress(e.target.value)}
              placeholder="Adresse complete" className="w-full border border-[#E0DFDD] p-2 text-sm mb-3" data-testid="exchange-address" />
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={deliveryType === 'home'} onChange={() => setDeliveryType('home')} className="accent-[#1A1514]" />
                Domicile ({fees.tarif_domicile || 0} DA)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={deliveryType === 'bureau'} onChange={() => setDeliveryType('bureau')} className="accent-[#1A1514]" />
                Bureau ({fees.tarif_bureau || 0} DA)
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer mb-2">
              <input type="checkbox" checked={freeShipping} onChange={e => setFreeShipping(e.target.checked)} className="mt-0.5 accent-[#1A1514]" data-testid="free-shipping-checkbox" />
              <span>Livraison <strong>gratuite</strong> (faute de notre part)</span>
            </label>
            {freeShipping && (
              <input type="text" value={freeReason} onChange={e => setFreeReason(e.target.value)}
                placeholder="Raison (ex: erreur de taille envoyee)"
                className="w-full border border-[#E0DFDD] p-2 text-sm" data-testid="free-reason" />
            )}
          </section>

          {/* Refund method (if negative diff) */}
          {totals.refundAmount > 0 && (
            <section className="mb-6 p-4 bg-amber-50 border border-amber-200">
              <h3 className="text-sm font-medium mb-2">Remboursement de {totals.refundAmount.toLocaleString()} DA</h3>
              <select value={refundMethod} onChange={e => setRefundMethod(e.target.value)} className="border border-amber-300 p-2 text-sm w-full" data-testid="refund-method">
                <option value="credit">Avoir / crédit sur compte cliente</option>
                <option value="cash">Cash à la livraison (livreur Ecotrack)</option>
              </select>
            </section>
          )}

          {/* Step 4: Summary */}
          <section className="mb-6 p-4 bg-[#F9F8F6] border border-[#E0DFDD]" data-testid="exchange-summary">
            <h3 className="text-sm font-medium mb-3 uppercase tracking-wide text-[#6B6A68]">Récapitulatif</h3>
            <div className="space-y-1 text-sm">
              <Row label="Articles d'échange" value={`${totals.newTotal.toLocaleString()} DA`} />
              <Row label="Articles retournés" value={`-${totals.returnedTotal.toLocaleString()} DA`} />
              <Row label="Différence" value={`${totals.diff >= 0 ? '+' : ''}${totals.diff.toLocaleString()} DA`} bold={totals.diff !== 0} />
              <Row label={`Livraison${freeShipping ? ' (gratuite)' : ''}`} value={`${totals.shippingCost.toLocaleString()} DA`} />
              <hr className="my-2 border-[#E0DFDD]" />
              {totals.customerPays > 0 ? (
                <Row label="Total à payer par cliente (COD)" value={`${totals.customerPays.toLocaleString()} DA`} bold large />
              ) : totals.refundAmount > 0 ? (
                <Row label={`À rembourser (${refundMethod === 'credit' ? 'avoir' : 'cash'})`} value={`${totals.refundAmount.toLocaleString()} DA`} bold large />
              ) : (
                <Row label="Échange équilibré" value="0 DA" bold large />
              )}
            </div>
          </section>

          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-3 justify-end">
              <button onClick={onClose} disabled={submitting} className="px-5 py-2 text-sm border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40">Annuler</button>
              <button onClick={handleSubmit} disabled={!canSubmit || submitting} className="px-5 py-2 text-sm bg-[#1A1514] text-white hover:bg-[#2A2524] disabled:opacity-40 flex items-center gap-2" data-testid="create-exchange-btn">
                {submitting && <Loader2 size={14} className="animate-spin" />}
                Créer l'échange & expédier
              </button>
            </div>
            {!canSubmit && !submitting && (
              <p className="text-xs text-red-600" data-testid="submit-help-text">
                Pour activer le bouton :{' '}
                {newItems.length === 0
                  ? "ajoutez au moins un produit d'échange"
                  : !wilaya
                    ? 'sélectionnez une wilaya'
                    : !commune
                      ? 'sélectionnez une commune'
                      : !shippingAddress
                        ? "saisissez l'adresse de livraison"
                        : (freeShipping && !freeReason.trim())
                          ? 'saisissez la raison de la livraison gratuite'
                          : 'complétez les champs requis'}
              </p>
            )}
            {submitError && !submitting && (
              <div
                className="w-full mt-1 p-3 border border-red-300 bg-red-50 text-xs text-red-800 flex items-start justify-between gap-3"
                data-testid="exchange-submit-error"
                role="alert"
              >
                <span className="break-words">
                  <strong className="font-medium">Échec de l'envoi :</strong> {submitError}
                </span>
                <button
                  onClick={() => setSubmitError('')}
                  className="text-red-600 hover:text-red-800 shrink-0"
                  aria-label="Fermer"
                  data-testid="exchange-submit-error-dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, large }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold' : ''} ${large ? 'text-base' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ProductPicker({ product, onPick }) {
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const variants = product.variants || [];
  const sizes = [...new Set(variants.filter(v => (v.stock || 0) > 0).map(v => v.size).filter(Boolean))];
  const colorsForSize = [...new Set(variants.filter(v => v.size === size && (v.stock || 0) > 0).map(v => v.color).filter(Boolean))];
  const hasColors = colorsForSize.length > 0;
  return (
    <div className="flex items-center gap-3 p-2 border-b border-[#E0DFDD] last:border-b-0">
      {product.images?.[0]?.url && <img src={product.images[0].url} alt="" className="w-10 h-10 object-cover" />}
      <div className="flex-1 text-sm">{product.name}</div>
      <select value={size} onChange={e => { setSize(e.target.value); setColor(''); }} className="border border-[#E0DFDD] p-1 text-xs">
        <option value="">Taille</option>
        {sizes.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      {size && hasColors && (
        <select value={color} onChange={e => setColor(e.target.value)} className="border border-[#E0DFDD] p-1 text-xs">
          <option value="">Couleur</option>
          {colorsForSize.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      <button
        disabled={!size || (hasColors && !color)}
        onClick={() => onPick(size, color)}
        className="px-2 py-1 text-xs bg-[#1A1514] text-white disabled:opacity-40"
        data-testid={`pick-${product.product_id}`}
      >
        Ajouter
      </button>
    </div>
  );
}
